import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Star, CheckCircle, ArrowLeft, ThumbsUp, ThumbsDown, Sparkles, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeedbackPage = () => {
  const { t } = useTranslation();
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [warranty, setWarranty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWarranty = async () => {
      try {
        // Use public endpoint that doesn't require auth
        const response = await axios.get(`${API_URL}/api/warranty/${requestId}/public`);
        setWarranty(response.data);
        
        // Check if feedback already submitted
        if (response.data.feedback_submitted) {
          setSubmitted(true);
        }
      } catch (err) {
        console.error('Error fetching warranty:', err);
        if (err.response?.status === 404) {
          setError('not_found');
        } else {
          setError('unknown');
        }
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchWarranty();
    }
  }, [requestId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Por favor, selecciona una valoración');
      return;
    }
    
    if (wouldRecommend === null) {
      toast.error('Por favor, indica si nos recomendarías');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/warranty/${requestId}/feedback`, {
        rating,
        comment: comment.trim() || null,
        would_recommend: wouldRecommend
      });
      
      setSubmitted(true);
      toast.success('¡Gracias por tu valoración!');
    } catch (err) {
      console.error('Error submitting feedback:', err);
      if (err.response?.status === 400) {
        toast.error('Ya has enviado una valoración para esta reparación');
        setSubmitted(true);
      } else {
        toast.error('Error al enviar la valoración');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F5F7] to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0047FF]"></div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F5F7] to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="text-xl font-semibold mb-2">Solicitud no encontrada</h2>
            <p className="text-[#52525B] mb-6">
              No pudimos encontrar esta solicitud de garantía. El enlace puede haber expirado o ser incorrecto.
            </p>
            <Link to="/">
              <Button>Volver al inicio</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F5F7] to-white flex items-center justify-center p-4" data-testid="feedback-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="max-w-md w-full text-center">
            <CardContent className="pt-8 pb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
              >
                <CheckCircle className="w-10 h-10 text-white" />
              </motion.div>
              
              <h2 className="text-2xl font-bold mb-3">¡Gracias por tu valoración!</h2>
              <p className="text-[#52525B] mb-6">
                Tu opinión nos ayuda a mejorar cada día. Esperamos verte pronto.
              </p>
              
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-amber-600">
                  <Sparkles className="w-5 h-5" />
                  <span className="font-medium">¡Tu reparación tiene 3 meses de garantía!</span>
                </div>
              </div>
              
              <div className="flex gap-3 justify-center">
                <Link to="/">
                  <Button variant="outline">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Volver al inicio
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button className="bg-[#0047FF] hover:bg-[#003BCC]">
                    Mi panel
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F5F7] to-white py-12 px-4" data-testid="feedback-page">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-8">
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#0A0A0A] transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <Card className="overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-[#0047FF] to-[#8B5CF6] p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Heart className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">¿Cómo fue tu experiencia?</h1>
                <p className="text-white/80">Tu opinión nos ayuda a mejorar</p>
              </div>
            </div>
          </div>

          <CardContent className="p-6">
            {/* Warranty Info */}
            {warranty && (
              <div className="bg-[#F5F5F7] rounded-xl p-4 mb-6">
                <p className="text-sm text-[#52525B] mb-1">Reparación completada</p>
                <p className="font-semibold">{warranty.product_name}</p>
                <p className="text-xs text-[#A1A1AA] font-mono mt-1">
                  Ref: #{requestId?.slice(0, 12).toUpperCase()}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Star Rating */}
              <div className="text-center">
                <label className="block text-sm font-medium text-[#0A0A0A] mb-4">
                  ¿Cómo valorarías nuestro servicio?
                </label>
                <div className="flex justify-center gap-2" data-testid="star-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.button
                      key={star}
                      type="button"
                      whileHover={{ scale: 1.15 }}
                      whileTap={{ scale: 0.95 }}
                      className="focus:outline-none"
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setRating(star)}
                      data-testid={`star-${star}`}
                    >
                      <Star
                        className={`w-10 h-10 transition-all duration-200 ${
                          (hoverRating || rating) >= star
                            ? 'fill-amber-400 text-amber-400 drop-shadow-md'
                            : 'text-gray-300'
                        }`}
                      />
                    </motion.button>
                  ))}
                </div>
                <AnimatePresence mode="wait">
                  {rating > 0 && (
                    <motion.p
                      key={rating}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="mt-3 text-sm font-medium text-[#52525B]"
                    >
                      {rating === 5 && "¡Excelente! 🎉"}
                      {rating === 4 && "¡Muy bien! 😊"}
                      {rating === 3 && "Bien 👍"}
                      {rating === 2 && "Regular 😐"}
                      {rating === 1 && "Necesitamos mejorar 😔"}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Would Recommend */}
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-4 text-center">
                  ¿Nos recomendarías a un amigo o familiar?
                </label>
                <div className="flex justify-center gap-4" data-testid="recommend-buttons">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setWouldRecommend(true)}
                    className={`flex items-center gap-3 px-6 py-4 rounded-xl border-2 transition-all ${
                      wouldRecommend === true
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="recommend-yes"
                  >
                    <ThumbsUp className={`w-6 h-6 ${wouldRecommend === true ? 'text-green-500' : 'text-gray-400'}`} />
                    <span className="font-medium">Sí, claro</span>
                  </motion.button>
                  
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setWouldRecommend(false)}
                    className={`flex items-center gap-3 px-6 py-4 rounded-xl border-2 transition-all ${
                      wouldRecommend === false
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="recommend-no"
                  >
                    <ThumbsDown className={`w-6 h-6 ${wouldRecommend === false ? 'text-red-500' : 'text-gray-400'}`} />
                    <span className="font-medium">Quizá no</span>
                  </motion.button>
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                  ¿Algo más que quieras contarnos? (opcional)
                </label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Cuéntanos tu experiencia, sugerencias o cualquier comentario..."
                  className="min-h-[120px] resize-none"
                  data-testid="feedback-comment"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#0047FF] to-[#8B5CF6] hover:opacity-90 h-12 text-lg"
                disabled={submitting || rating === 0 || wouldRecommend === null}
                data-testid="submit-feedback"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  'Enviar valoración'
                )}
              </Button>
            </form>

            {/* Footer Note */}
            <p className="text-xs text-center text-[#A1A1AA] mt-6">
              Tu valoración es anónima y nos ayuda a mejorar nuestro servicio.
              <br />
              Gracias por confiar en Arreglatelo.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default FeedbackPage;
